"use client"

import { cn } from '@/lib/utils'

interface FadeControlsProps {
  fadeIn: number
  fadeOut: number
  maxDuration?: number
  onFadeInChange: (value: number) => void
  onFadeOutChange: (value: number) => void
  disabled?: boolean
  className?: string
}

const FADE_PRESETS = [0, 0.05, 0.1, 0.2, 0.3, 0.5]

export function FadeControls({
  fadeIn,
  fadeOut,
  maxDuration = 5,
  onFadeInChange,
  onFadeOutChange,
  disabled = false,
  className,
}: FadeControlsProps) {
  // Max fade duration is half of the total (so both can be applied)
  const maxFade = Math.min(0.5, maxDuration / 2)

  const formatFade = (value: number) => {
    if (value === 0) return 'Off'
    return `${(value * 1000).toFixed(0)}ms`
  }

  return (
    <div className={cn("space-y-4 rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] p-4", className)}>
      <p className="flex items-center gap-2 text-sm text-[var(--tls-fg-muted)] m-0">
        <span>Fades</span>
        <span className="text-xs text-[var(--tls-fg-subtle)]">(prevents clicks at cut points)</span>
      </p>

      <div className="grid grid-cols-2 gap-4">
        {/* Fade In */}
        {/* fieldset/legend groups the label with its preset buttons so AT reads them together */}
        <fieldset className="space-y-2 border-0 p-0 m-0">
          <legend className="flex items-center gap-2 text-sm text-[var(--tls-fg-muted)] w-full">
            <span>Fade in</span>
            <span className="ml-auto font-medium text-[var(--tls-fg-strong)]">{formatFade(fadeIn)}</span>
          </legend>

          <div className="flex flex-wrap gap-1">
            {FADE_PRESETS.flatMap((preset) => preset > maxFade ? [] : [
              <button type="button"
                key={`in-${preset}`}
                onClick={() => onFadeInChange(preset)}
                disabled={disabled}
                aria-pressed={fadeIn === preset}
                className={cn(
                  // min-h ensures 44px WCAG 2.5.5 touch target
                  "min-h-[44px] min-w-[3rem] rounded-[var(--ls-radius-0)] px-2 py-1.5 text-xs transition-colors duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
                  fadeIn === preset
                    ? "border border-[color:color-mix(in_srgb,var(--tls-brand)_24%,transparent)] bg-[color:color-mix(in_srgb,var(--tls-brand)_10%,var(--tls-surface))] text-[var(--tls-brand)]"
                    : "border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] text-[var(--tls-fg-muted)] hover:border-[color:var(--tls-border-strong)] hover:text-[var(--tls-fg-strong)]",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {preset === 0 ? 'Off' : `${(preset * 1000).toFixed(0)}ms`}
              </button>
            ])}
          </div>
        </fieldset>

        {/* Fade Out */}
        <fieldset className="space-y-2 border-0 p-0 m-0">
          <legend className="flex items-center gap-2 text-sm text-[var(--tls-fg-muted)] w-full">
            <span>Fade out</span>
            <span className="ml-auto font-medium text-[var(--tls-fg-strong)]">{formatFade(fadeOut)}</span>
          </legend>

          <div className="flex flex-wrap gap-1">
            {FADE_PRESETS.flatMap((preset) => preset > maxFade ? [] : [
              <button type="button"
                key={`out-${preset}`}
                onClick={() => onFadeOutChange(preset)}
                disabled={disabled}
                aria-pressed={fadeOut === preset}
                className={cn(
                  // min-h ensures 44px WCAG 2.5.5 touch target
                  "min-h-[44px] min-w-[3rem] rounded-[var(--ls-radius-0)] px-2 py-1.5 text-xs transition-colors duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
                  fadeOut === preset
                    ? "border border-[color:color-mix(in_srgb,var(--tls-brand)_24%,transparent)] bg-[color:color-mix(in_srgb,var(--tls-brand)_10%,var(--tls-surface))] text-[var(--tls-brand)]"
                    : "border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] text-[var(--tls-fg-muted)] hover:border-[color:var(--tls-border-strong)] hover:text-[var(--tls-fg-strong)]",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {preset === 0 ? 'Off' : `${(preset * 1000).toFixed(0)}ms`}
              </button>
            ])}
          </div>
        </fieldset>
      </div>
    </div>
  )
}
